// src/pages/admin/ContactMessageDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IconPencil,
  IconCheck,
  IconX,
  IconUser,
  IconMail,
  IconInfoCircle,
  IconPaperclip,
  IconClock,
  IconDeviceDesktop,
  IconBrowser
} from "@tabler/icons-react";
import { useSelector, useDispatch } from "react-redux";
import { 
  getSingleContactUsMessage, 
  updateContactUsMessage 
} from "../../redux/slices/contactUsSlice";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import ErrorFallback from "../../components/common/ErrorFallback";
import LoadingFallback from "../../components/common/LoadingFallback";

const ContactMessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  // Redux state
  const { singleMessage: message, loading, error } = useSelector((state) => state.contactUs);

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [isProcessed, setIsProcessed] = useState(false);

  // Fetch message details on mount
  useEffect(() => {
    setLayoutConfig({
      title: "Contact Ud Message / Detail",
      showBackButton: true,
      scrollable: true,
    });
    dispatch(getSingleContactUsMessage(id));
  }, [dispatch, id, setLayoutConfig]);

  // Initialize form fields when message loads
  useEffect(() => {
    if (message) {
      setAdminNotes(message.admin_notes || "");
      setIsProcessed(message.is_processed || false);
    }
  }, [message]);

  const handleSave = async () => {
    try {
      await dispatch(updateContactUsMessage({
        contact_us_id:id,
        admin_notes: adminNotes,
        is_processed: isProcessed
      })).unwrap();
      setIsEditing(false);
      dispatch(getSingleContactUsMessage(id)); // Refresh the data
    } catch (error) {
      console.error('Failed to update message:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) return <LoadingFallback />;
  if (error) return <ErrorFallback error={error} onRetry={() => dispatch(getSingleContactUsMessage(id))} />;

  return (
    <div>
      <div className="flex-1 flex flex-col">
        <main className="p-6 flex-1 overflow-y-auto">
          {/* Message Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-xl font-semibold">
                Message from {message?.full_name}
              </h1>
              <div className={`px-3 py-1 rounded-full text-sm ${
                message?.is_processed 
                  ? "bg-green-100 text-green-800" 
                  : "bg-yellow-100 text-yellow-800"
              }`}>
                {message?.is_processed ? "Processed" : "Pending"}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Sender Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                  <IconUser size={20} />
                  Sender Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="font-medium">{message?.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{message?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Profile Picture</p>
                    {message?.profile_picture ? (
                      <img 
                        src={message.profile_picture} 
                        alt="Profile" 
                        className="w-16 h-16 rounded-full object-cover mt-1"
                      />
                    ) : (
                      <p className="font-medium">N/A</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Message Metadata */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                  <IconInfoCircle size={20} />
                  Message Details
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Reason</p>
                    <p className="font-medium">{message?.reason || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Order Number</p>
                    <p className="font-medium">{message?.order_number || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Submitted At</p>
                    <p className="font-medium">{formatDate(message?.submitted_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Processed At</p>
                    <p className="font-medium">{formatDate(message?.processed_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Message Content */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold border-b pb-2 mb-4">Message Content</h3>
            <div className="whitespace-pre-line p-4 bg-gray-50 rounded">
              {message?.message}
            </div>
          </div>

          {/* Attachments */}
          {message?.attachments?.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center gap-2">
                <IconPaperclip size={20} />
                Attachments ({message.attachments.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {message.attachments.map((attachment, index) => (
                  <div key={index} className="border rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-gray-100 p-2 rounded">
                        <IconPaperclip size={20} className="text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {attachment.file.split('/').pop()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(attachment.uploaded_at)}
                        </p>
                      </div>
                    </div>
                    <a 
                      href={attachment.file} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-sm text-blue-500 hover:underline"
                    >
                      View File
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Technical Details */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold border-b pb-2 mb-4 flex items-center gap-2">
              <IconDeviceDesktop size={20} />
              Technical Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">IP Address</p>
                <p className="font-mono">{message?.ip_address || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">User Agent</p>
                <p className="font-mono text-sm">{message?.user_agent || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Admin Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold border-b pb-2 mb-4">Admin Notes</h3>
            
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={isProcessed}
                        onChange={(e) => setIsProcessed(e.target.checked)}
                        className="form-checkbox h-5 w-5 text-blue-600 rounded"
                      />
                      <span className="ml-2">Mark as processed</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="w-full border rounded px-3 py-2 min-h-[100px]"
                    placeholder="Add any notes about this message..."
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-gray-300 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-500 text-white rounded flex items-center gap-1"
                  >
                    <IconCheck size={18} />
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium">
                    {message?.is_processed ? "Processed" : "Pending"}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Admin Notes</p>
                  <div className="p-3 bg-gray-50 rounded whitespace-pre-line">
                    {message?.admin_notes || "No notes added"}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1 bg-blue-500 text-white rounded flex items-center gap-1"
                  >
                    <IconPencil size={16} />
                    Edit
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ContactMessageDetail;